// @owners { team: patients-team }
import { BORDERS, COLORS, SIZES, SPACING } from '@alto/design-library-tokens';
import { XsSpacing } from '@alto/design-system';
import { useAddOnsContext } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import { type AddOnOtc } from '@alto/scriptdash/alto/patient_app/add_ons/types/v1/add_on_otc';
import React from 'react';
import styled from 'styled-components';
import { type AnalyticsProps } from '~shared/features/essentials/AnalyticsProps';
import { getShipmentAddress } from '~shared/features/shipments/selectors/getShipments';
import { useSelectorShared } from '~shared/store';
import { Carousel, StoreProductTag } from '~web/features/essentials/components';
import { EssentialTag } from '~web/features/essentials/components/EssentialsTag';
import { Image, StyledTouchable, type StyledTouchableProps, Text, View } from '~web/features/essentials/primitives';

const StyledProductItem = styled.div<StyledTouchableProps>`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  border-radius: ${BORDERS.RADIUS.LG.px};
  padding: ${SPACING.STATIC.SM.px};
  &:hover {
    background-color: ${COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTER};
  }
  ${StyledTouchable}
`;

const StyledImage = styled(View)<StyledTouchableProps>`
  ${StyledTouchable}
`;

type ProductCarouselProps = {
  readonly numOfItems: number;
  readonly numOfColumns: number;
  readonly products: AddOnOtc[];
  readonly categoryName: string;
};

const ProductCarouselItem = ({
  product,
  numOfColumns,
  analyticsProps,
}: {
  readonly product: AddOnOtc;
  readonly numOfColumns: number;
  readonly analyticsProps: AnalyticsProps;
}) => {
  const { navigate } = useNavigation();
  const { shipmentID, origin } = useAddOnsContext();
  const onPress = (id: number) => () => {
    navigate('RouteEssentialsDetails', {
      addOnOtc: product,
      productID: id,
      search: `?order=${shipmentID}&origin=${origin}`,
      analyticsProps,
    });
  };
  return (
    <Carousel.Item
      numOfColumns={numOfColumns}
      key={product.name}
    >
      <StyledProductItem onClick={onPress(product.product_id)}>
        <StyledImage
          position="sticky"
          justifyContent="center"
        >
          <Image
            src={product.image_urls[0]}
            width={SIZES.ILLUSTRATION.LG.px}
            height={SIZES.ILLUSTRATION.LG.px}
            alt={`icon for ${product.name}`}
          />
          <StoreProductTag
            product={product}
            shipmentID={shipmentID}
          />
        </StyledImage>
        <EssentialTag addOnOtc={product} />
        <Text
          title={product.quantity_description || ''}
          color="DISABLED"
        />
        <XsSpacing />
        <Text
          title={product.name}
          numberOfLines={3}
          color="PRIMARY"
          variant="body-semibold"
        />
        <View flexGrow={1} />
        <XsSpacing />
        <Text
          title={`$${product.price.toFixed(2)}`}
          color="PRIMARY"
        />
      </StyledProductItem>
    </Carousel.Item>
  );
};

export const ProductCarousel = ({ numOfItems, numOfColumns, products, categoryName }: ProductCarouselProps) => {
  const { shipmentID, origin } = useAddOnsContext();
  const address = useSelectorShared((state) => getShipmentAddress(state, { shipmentID }));
  return (
    <Carousel.Container
      numOfItems={numOfItems}
      numOfColumns={numOfColumns}
    >
      {products.map((product, i) => (
        <ProductCarouselItem
          key={product.product_id}
          product={product}
          numOfColumns={numOfColumns}
          analyticsProps={{
            slot: i + 1,
            sku: product.sku,
            sku_type: product.sku_type,
            otc_name: product.name,
            category: categoryName,
            query_string: undefined,
            shipment_id: shipmentID,
            facility_id: address?.dispensing_facility_id,
            origin,
          }}
        />
      ))}
    </Carousel.Container>
  );
};
